import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { coverBranchIDToFullName } from '../../utils/Branch/SupportBranch';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;

const pageStyle = {
    minHeight: '100vh',
    padding: '24px',
    background: 'linear-gradient(180deg, #f4f7fb 0%, #eef3f8 100%)',
};

const cardStyle = {
    background: '#fff',
    borderRadius: '18px',
    padding: '18px',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
    border: '1px solid rgba(148, 163, 184, 0.18)',
};

const getEventBadgeStyle = (type) => {
    switch (type) {
        case 'client-join':
            return { background: '#dcfce7', color: '#166534' };
        case 'client-leave':
        case 'client-disconnect':
            return { background: '#fee2e2', color: '#991b1b' };
        case 'booking-new':
            return { background: '#dbeafe', color: '#1d4ed8' };
        case 'booking-waiting':
            return { background: '#fef3c7', color: '#92400e' };
        case 'booking-update':
            return { background: '#ede9fe', color: '#6d28d9' };
        case 'booking-redo':
            return { background: '#fce7f3', color: '#9d174d' };
        default:
            return { background: '#e5e7eb', color: '#374151' };
    }
};

const formatEventLabel = (type) => {
    switch (type) {
        case 'client-join': return 'JOIN';
        case 'client-leave': return 'LEAVE';
        case 'client-disconnect': return 'DISCONNECT';
        case 'booking-new': return 'NEW BOOKING';
        case 'booking-waiting': return 'WAITING';
        case 'booking-update': return 'UPDATE';
        case 'booking-redo': return 'REDO';
        default: return (type || 'EVENT').toUpperCase();
    }
};

const filterButtonStyle = (active) => ({
    border: 'none',
    borderRadius: '999px',
    padding: '8px 14px',
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer',
    background: active ? '#0f172a' : '#e2e8f0',
    color: active ? '#fff' : '#334155',
});

const formatVNDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    const formatter = new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'Asia/Ho_Chi_Minh',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const getPart = (type) => parts.find((part) => part.type === type)?.value || '';

    return `${getPart('year')}-${getPart('month')}-${getPart('day')} @${getPart('hour')}:${getPart('minute')}`;
};

const formatTimeAgoVN = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return `vừa xong · ${formatVNDateTime(value)}`;

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let relativeText = 'vừa xong';

    if (diffMinutes < 1) {
        relativeText = 'vừa xong';
    } else if (diffMinutes < 60) {
        relativeText = `${diffMinutes} phút trước`;
    } else if (diffHours < 24) {
        relativeText = `${diffHours} giờ trước`;
    } else {
        relativeText = `${diffDays} ngày trước`;
    }

    return `${relativeText} · ${formatVNDateTime(value)}`;
};

function RealtimeAdminContainer() {
    const auth = useSelector((state) => state.auth);
    const token = useSelector((state) => state.token);
    const { role, name } = auth.user || {};
    const [clients, setClients] = useState([]);
    const [events, setEvents] = useState([]);
    const [connected, setConnected] = useState(false);
    const [feedFilter, setFeedFilter] = useState('all');
    const [sourceMode, setSourceMode] = useState('reservation-only');
    const [selectedBranchID, setSelectedBranchID] = useState('all');
    const [autoScroll, setAutoScroll] = useState(true);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);
    const feedTopRef = useRef(null);

    const isAllowed = true;
    const isTabletOrMobile = windowWidth <= 1100;
    const isMobile = windowWidth <= 768;

    const branchOptions = useMemo(() => {
        const branchMap = new Map();

        clients.forEach((item) => {
            if (item?.branchID !== undefined && item?.branchID !== null && item?.branchID !== '') {
                branchMap.set(String(item.branchID), coverBranchIDToFullName(Number(item.branchID)) || `Branch ${item.branchID}`);
            }
        });

        events.forEach((item) => {
            if (item?.branchID !== undefined && item?.branchID !== null && item?.branchID !== '') {
                branchMap.set(String(item.branchID), coverBranchIDToFullName(Number(item.branchID)) || `Branch ${item.branchID}`);
            }
        });

        return Array.from(branchMap.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => Number(a.value) - Number(b.value));
    }, [clients, events]);

    const scopedClients = useMemo(() => {
        let nextClients = sourceMode === 'all'
            ? clients
            : clients.filter((item) => item?.source === 'reservation-realtime');

        if (selectedBranchID !== 'all') {
            nextClients = nextClients.filter((item) => String(item?.branchID) === String(selectedBranchID));
        }

        return nextClients;
    }, [clients, sourceMode, selectedBranchID]);

    const scopedEvents = useMemo(() => {
        let nextEvents = sourceMode === 'all'
            ? events
            : events.filter((item) => {
                if (String(item?.type || '').startsWith('booking-')) return true;
                return item?.client?.source === 'reservation-realtime';
            });

        if (selectedBranchID !== 'all') {
            nextEvents = nextEvents.filter((item) => String(item?.branchID) === String(selectedBranchID));
        }

        return nextEvents;
    }, [events, sourceMode, selectedBranchID]);

    const summary = useMemo(() => {
        const joinCount = scopedEvents.filter((item) => item?.type === 'client-join').length;
        const disconnectCount = scopedEvents.filter((item) => item?.type === 'client-leave' || item?.type === 'client-disconnect').length;
        const bookingCount = scopedEvents.filter((item) => String(item?.type || '').startsWith('booking-')).length;
        return { joinCount, disconnectCount, bookingCount };
    }, [scopedEvents]);

    const filteredEvents = useMemo(() => {
        if (feedFilter === 'join') {
            return scopedEvents.filter((item) => item?.type === 'client-join');
        }
        if (feedFilter === 'disconnect') {
            return scopedEvents.filter((item) => item?.type === 'client-leave' || item?.type === 'client-disconnect');
        }
        if (feedFilter === 'booking') {
            return scopedEvents.filter((item) => String(item?.type || '').startsWith('booking-'));
        }
        return scopedEvents;
    }, [scopedEvents, feedFilter]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!token || !isAllowed) return;

        const socket = io(SOCKET_URL, { reconnection: false });
        // const socket = io("https://database.azumayareport.com/",{  reconnection: false,})

        socket.on('connect', () => {
            setConnected(true);
            socket.emit('join_realtime_admin', {
                userName: name || '',
                role: role ?? null,
                page: window.location.pathname,
                source: 'realtime-admin',
            });
        });

        socket.on('disconnect', () => setConnected(false));
        socket.on('realtime_admin:init', (data) => {
            setClients(data?.clients || []);
            setEvents(data?.events || []);
        });
        socket.on('realtime_admin:clients', (data) => {
            setClients(data || []);
        });
        socket.on('realtime_admin:event', (data) => {
            setEvents((prev) => [data, ...prev].slice(0, 200));
        });

        return () => socket.disconnect();
    }, [token, isAllowed, role, name]);

    useEffect(() => {
        if (!autoScroll) return;
        feedTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [filteredEvents, autoScroll]);

    const openBooking = (event) => {
        const bookingId = Array.isArray(event?.bookingIds) ? event.bookingIds[0] : '';
        const branchID = event?.branchID;
        if (!bookingId || branchID === undefined || branchID === null) return;
        window.open(`/room/${branchID}/${bookingId}`, '_blank');
    };

    if (!isAllowed) {
        return (
            <div style={{ padding: '24px' }}>
                <h2>Realtime Admin</h2>
                <p>You do not have permission to access this page.</p>
            </div>
        );
    }

    return (
        <div style={pageStyle}>
            <div style={{ width: '100%', maxWidth: '1440px', margin: '0 auto' }}>
                <div style={{ ...cardStyle, marginBottom: '18px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ fontSize: '30px', fontWeight: 800, letterSpacing: '-0.03em' }}>Realtime Admin</div>
                            <div style={{ color: 'rgba(255,255,255,0.78)', marginTop: '6px' }}>
                                Default focus: realtime activity from <strong>/reservation</strong>. You can still switch to view all realtime sources.
                            </div>
                        </div>
                        <div style={{
                            padding: '10px 14px',
                            borderRadius: '999px',
                            background: connected ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.18)',
                            color: connected ? '#86efac' : '#fca5a5',
                            fontWeight: 700,
                            border: `1px solid ${connected ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
                        }}>
                            {connected ? '● Live connected' : '● Disconnected'}
                        </div>
                    </div>
                </div>

                <div style={{ ...cardStyle, marginBottom: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: '16px', color: '#0f172a' }}>Realtime Source Scope</div>
                        <div style={{ color: '#64748b', marginTop: '4px' }}>Reservation view is the default because that is your main monitoring target.</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button type="button" style={filterButtonStyle(sourceMode === 'reservation-only')} onClick={() => setSourceMode('reservation-only')}>
                            Reservation Only
                        </button>
                        <button type="button" style={filterButtonStyle(sourceMode === 'all')} onClick={() => setSourceMode('all')}>
                            All Realtime Sources
                        </button>
                        <select
                            value={selectedBranchID}
                            onChange={(event) => setSelectedBranchID(event.target.value)}
                            style={{
                                border: '1px solid #cbd5e1',
                                borderRadius: '999px',
                                padding: '8px 14px',
                                fontWeight: 700,
                                fontSize: '13px',
                                background: '#fff',
                                color: '#0f172a',
                                minWidth: '220px'
                            }}
                        >
                            <option value="all">All Branches</option>
                            {branchOptions.map((branch) => (
                                <option key={branch.value} value={branch.value}>
                                    {branch.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTabletOrMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '14px', marginBottom: '18px' }}>
                    {[
                        { label: 'Online Clients', value: scopedClients.length, color: '#2563eb' },
                        { label: 'Join Events', value: summary.joinCount, color: '#16a34a' },
                        { label: 'Disconnect Events', value: summary.disconnectCount, color: '#dc2626' },
                        { label: 'Booking Events', value: summary.bookingCount, color: '#7c3aed' },
                    ].map((item) => (
                        <div key={item.label} style={{ ...cardStyle, padding: '16px' }}>
                            <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>{item.label}</div>
                            <div style={{ fontSize: '30px', fontWeight: 800, color: item.color }}>{item.value}</div>
                        </div>
                    ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isTabletOrMobile ? '1fr' : '1fr 1.6fr', gap: '18px', alignItems: 'start' }}>
                    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: isTabletOrMobile ? '50vh' : '70vh' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexShrink: 0, position: 'sticky', top: 0, zIndex: 1, background: '#fff', paddingBottom: '8px' }}>
                            <strong style={{ fontSize: '18px' }}>Online Clients</strong>
                            <span style={{ color: '#64748b', fontWeight: 700 }}>{scopedClients.length}</span>
                        </div>

                        <div style={{ overflowY: 'auto', overflowX: 'hidden', paddingRight: '6px', minHeight: 0, overscrollBehavior: 'contain' }}>
                            {scopedClients.length === 0 ? (
                                <div style={{ color: '#777' }}>No realtime clients in this scope.</div>
                            ) : scopedClients.map((client) => (
                                <div key={client.socketId} style={{
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '14px',
                                    padding: '14px',
                                    marginBottom: '12px',
                                    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                                        <div style={{ fontWeight: 800, color: '#0f172a' }}>{client.userName || 'Unknown user'}</div>
                                        <span style={{
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            padding: '4px 10px',
                                            borderRadius: '999px',
                                            background: '#dcfce7',
                                            color: '#166534'
                                        }}>
                                            ONLINE
                                        </span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', fontSize: '14px' }}>
                                        <div><strong>Branch:</strong> {client.branchID ? (coverBranchIDToFullName(Number(client.branchID)) || `Branch ${client.branchID}`) : '-'}</div>
                                        <div><strong>Role:</strong> {client.role ?? '-'}</div>
                                        <div><strong>Page:</strong> {client.page || '-'}</div>
                                        <div><strong>Source:</strong> {client.source || '-'}</div>
                                    </div>
                                    <div style={{ color: '#64748b', fontSize: '12px', marginTop: '8px' }}>
                                        Last seen: {formatTimeAgoVN(client.lastSeenAt)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: isTabletOrMobile ? '50vh' : '70vh' }}>
                        <div ref={feedTopRef} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap', flexShrink: 0, position: 'sticky', top: 0, zIndex: 1, background: '#fff', paddingBottom: '8px' }}>
                            <strong style={{ fontSize: '18px' }}>Realtime Activity Feed</strong>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <button type="button" style={filterButtonStyle(feedFilter === 'all')} onClick={() => setFeedFilter('all')}>All</button>
                                <button type="button" style={filterButtonStyle(feedFilter === 'join')} onClick={() => setFeedFilter('join')}>Join</button>
                                <button type="button" style={filterButtonStyle(feedFilter === 'disconnect')} onClick={() => setFeedFilter('disconnect')}>Disconnect</button>
                                <button type="button" style={filterButtonStyle(feedFilter === 'booking')} onClick={() => setFeedFilter('booking')}>Booking</button>
                                <button
                                    type="button"
                                    style={{ ...filterButtonStyle(autoScroll), background: autoScroll ? '#16a34a' : '#e2e8f0', color: autoScroll ? '#fff' : '#334155' }}
                                    onClick={() => setAutoScroll((prev) => !prev)}
                                >
                                    {autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
                                </button>
                            </div>
                        </div>

                        <div style={{ overflowY: 'auto', overflowX: 'hidden', paddingRight: '6px', minHeight: 0, overscrollBehavior: 'contain' }}>
                            {filteredEvents.length === 0 ? (
                                <div style={{ color: '#777' }}>No realtime activity for this filter.</div>
                            ) : filteredEvents.map((event) => {
                                const badgeStyle = getEventBadgeStyle(event.type);
                                return (
                                    <div key={event.id} style={{
                                        border: '1px solid #e2e8f0',
                                        background: '#f8fafc',
                                        padding: '14px',
                                        marginBottom: '12px',
                                        borderRadius: '14px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                            <span style={{
                                                ...badgeStyle,
                                                fontSize: '12px',
                                                fontWeight: 800,
                                                padding: '5px 10px',
                                                borderRadius: '999px',
                                                letterSpacing: '0.02em'
                                            }}>
                                                {formatEventLabel(event.type)}
                                            </span>
                                            <span style={{ color: '#64748b', fontSize: '12px' }}>{formatTimeAgoVN(event.createdAt)}</span>
                                        </div>

                                        <div style={{ marginTop: '10px', fontWeight: 700, color: '#0f172a' }}>{event.message}</div>

                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                            {event.branchID !== undefined && (
                                                <span style={{ background: '#e2e8f0', color: '#334155', padding: '5px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700 }}>
                                                    {coverBranchIDToFullName(Number(event.branchID)) || `Branch ${event.branchID}`}
                                                </span>
                                            )}
                                            {Array.isArray(event.bookingIds) && event.bookingIds.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => openBooking(event)}
                                                    style={{
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        background: '#dbeafe',
                                                        color: '#1d4ed8',
                                                        padding: '5px 10px',
                                                        borderRadius: '999px',
                                                        fontSize: '12px',
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    Booking {event.bookingIds.join(', ')}
                                                </button>
                                            )}
                                            {event?.client?.source && (
                                                <span style={{ background: '#eef2ff', color: '#4338ca', padding: '5px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700 }}>
                                                    {event.client.source}
                                                </span>
                                            )}
                                        </div>

                                        {event.client && (
                                            <div style={{ marginTop: '10px', color: '#475569', fontSize: '13px' }}>
                                                <strong>Client:</strong> {event.client.userName || 'Unknown'} · {event.client.source || '-'} · {event.client.page || '-'}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RealtimeAdminContainer;
